import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { GetConnectResponse, GetConnectItem } from './types'
import { getConnect } from '../api/subscriptionRepository'
import { authorize } from '@/shared/lib/auth/authorize'
import { PostLogout } from '@/shared/repositories/LogoutRepository'

export type OSType = 'android' | 'ios' | 'macos' | 'windows' | 'linux'

export const useConnectStore = defineStore('connect', () => {
  const connectData = ref<GetConnectResponse | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const errorStatus = ref<string | null>(null)
  
  // Текущая выбранная операционная система
  const selectedOS = ref<OSType>('android')
  
  // Вычисляемые свойства для получения данных текущей ОС
  const currentApps = computed<GetConnectItem[]>(() => {
    if (!connectData.value) return []
    return connectData.value[selectedOS.value] || []
  })
  
  const subscriptionLink = computed<string>(() => {
    return connectData.value?.subscription_link || ''
  })
  
  // Определение доступных ОС (только те, у которых есть приложения)
  const availableOS = computed<OSType[]>(() => {
    if (!connectData.value) return []
    
    const osTypes: OSType[] = ['android', 'ios', 'macos', 'windows', 'linux']
    return osTypes.filter(os => {
      const apps = connectData.value![os]
      return apps && apps.length > 0
    })
  })

  async function fetchConnectData(subscriptionId: string) {
    if (isLoading.value) {
      console.log('Запрос инструкций подключения уже выполняется, пропускаем')
      return
    }

    try {
      isLoading.value = true
      error.value = null
      errorStatus.value = null
      connectData.value = null
      
      const response = await getConnect(subscriptionId)
      
      console.log('Connect API response:', response.data)
      connectData.value = response.data
      
      // Автоматически выбираем первую доступную ОС
      if (availableOS.value.length > 0 && !availableOS.value.includes(selectedOS.value)) {
        selectedOS.value = availableOS.value[0]
      }
      
    } catch (err: any) {
      error.value = err instanceof Error ? err.message : 'Неизвестная ошибка'
      connectData.value = null
      
      if (err.status === 401) {
        console.log('401 - неавторизован')
        errorStatus.value = err.status
        await PostLogout()
        await authorize()
      } else if (err.status === 404) {
        error.value = 'Подписка не найдена'
      } else if (err.status === 403) {
        error.value = 'Подписка не принадлежит пользователю'
      } else if (err.status === 400) {
        error.value = 'Инструкции доступны только для X-Ray подписок'
      }
    } finally {
      isLoading.value = false
    }
  }

  function setSelectedOS(os: OSType) {
    if (availableOS.value.includes(os)) {
      selectedOS.value = os
    }
  }

  function clearConnectData() {
    connectData.value = null
    error.value = null
    errorStatus.value = null
    selectedOS.value = 'android'
  }

  // Функция для копирования ссылки в буфер обмена
  async function copySubscriptionLink(): Promise<boolean> {
    if (!subscriptionLink.value) return false
    
    try {
      await navigator.clipboard.writeText(subscriptionLink.value)
      return true
    } catch (err) {
      console.error('Ошибка копирования в буфер обмена:', err)
      return false
    }
  }

  return {
    // State
    connectData,
    isLoading,
    error,
    errorStatus,
    selectedOS,
    
    // Computed
    currentApps,
    subscriptionLink,
    availableOS,
    
    // Actions
    fetchConnectData,
    setSelectedOS,
    clearConnectData,
    copySubscriptionLink
  }
})